import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, InputGroup, Spinner } from "react-bootstrap";
import { SetCookie } from "../../common/CookieHelper";
import { CartGateway } from "../../gateways/CartGateway";
import { useGlobal } from "../../contexts/GlobalContext";
import { Utils } from "../../common/Utils";
import { FireStoreProduct } from "../../model/ProducModel";
import { CategoryGateway } from "../../gateways/CategoryGateway";
import { ProductGateway } from "../../gateways/ProductGateway";
import { CartItem } from "../../model/CartModel";
import { GetCartCookieKey } from "../../gateways/SiteGateway";

const ProductView: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const { qty } = useParams<{ qty: string }>();
    const { hasattributes } = useParams<{ hasattributes: string }>();

    const [firestoreProduct, setFirestoreProduct] = useState<FireStoreProduct | null>(null);
    const [categoryName, setCategoryName] = useState<string>("Unknown");
    const [selectedAttributes, setSelectedAttributes] = useState<{ [key: string]: string }>({});
    const [quantity, setQuantity] = useState<number>(parseInt(qty ?? "1"));
    const [loading, setLoading] = useState<boolean>(true);
    const [addToCartLoading, setAddToCartLoading] = useState<boolean>(false);
    const [shownAttributesWarning, setShownAttributesWarning] = useState<boolean>(false);
    const { cart, updateCart, settings, user } = useGlobal();

    useEffect(() => {
        const fetchProduct = async () => {
            if (!productId) return;
            setLoading(true); // Start loading
            try {
                const productData = await ProductGateway.GetProduct(productId);
                if (productData) {
                    setFirestoreProduct({ product: productData, id: productId });

                    const category = await CategoryGateway.GetCategory(productData.category);
                    if (category) {
                        setCategoryName(category.name);
                    }
                }
            } catch (error) {
                console.error("Error fetching product:", error);
            } finally {
                setLoading(false); // Stop loading
            }
        };

        fetchProduct();
    }, [productId]);

    if (loading || !settings) {
        return (
            <Container className="main-container mt-4 text-center">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p>Fetching product details...</p>
            </Container>
        );
    }

    if (!firestoreProduct) return <p>Product not found.</p>;
    if (hasattributes && !shownAttributesWarning) {
        const attributes = Array.from(new Set(firestoreProduct.product.attributes?.map(attr => attr.name).sort() || []));

        if (attributes.length > 0) {
            alert(`Please select a value for: ${attributes.join(", ")}`);
        }
        setShownAttributesWarning(true);
    }

    const handleAddToCart = () => {
        const missingAttributes = Array.from(new Set(firestoreProduct.product.attributes?.map(attr => attr.name) || []))
            .filter((key) => !selectedAttributes[key]);

        if (missingAttributes.length > 0) {
            alert(`Please select a value for: ${missingAttributes.join(", ")}`);
            return;
        }

        setAddToCartLoading(true);
        setTimeout(async () => {
            const existingItem = cart.cartItems.find(item => ProductGateway.GetKey(item.id, item.attributes) === ProductGateway.GetKey(firestoreProduct.id, selectedAttributes));
            const updatedCart = existingItem
                ? cart.cartItems.map(item => (ProductGateway.GetKey(item.id, item.attributes) === ProductGateway.GetKey(firestoreProduct.id, selectedAttributes) ? { ...item, quantity: item.quantity + quantity, attributes: { ...selectedAttributes } } : item))
                : [...cart.cartItems, { id: firestoreProduct.id, name: firestoreProduct.product.name, attributes: { ...selectedAttributes }, quantity }];

            if (user)
                await CartGateway.SaveCart({ ...cart, cartItems: updatedCart as CartItem[] }, user);
            else
                SetCookie(GetCartCookieKey, JSON.stringify({ ...cart, cartItems: updatedCart }), 7);

            updateCart();
            setAddToCartLoading(false);
            console.log("Added to cart:", { firestoreProduct, selectedAttributes, quantity });
        }, 1000);
        //alert(`${quantity}x ${product?.name} added to cart!`);
    };


    const handleAttributeSelection = (key: string, value: string) => {
        setSelectedAttributes(prev => {
            const updated = { ...prev };
            updated[key] = value;
            return updated;
        });
    };


    return (
        <Container className="main-container mt-4">
            <Row>
                {
                    firestoreProduct.product.imageUrl && firestoreProduct.product.imageUrl !== "" &&
                    <Col md={4}>
                        <Card className="p-3 shadow-sm">
                            <Card.Img variant="top" src={firestoreProduct.product.imageUrl} />
                        </Card>
                    </Col>
                }
                <Col md={6}>
                    <h2>{firestoreProduct.product.name}</h2>
                    <p>{firestoreProduct.product.description}</p>
                    {settings.hasPrice && <h4>{Utils.FormatCurrency(firestoreProduct.product.price, settings.currency)}</h4>}
                    <p>Category: <strong>{categoryName}</strong></p>

                    {firestoreProduct.product.attributes && firestoreProduct.product.attributes.length > 0 ? (
                        <Form.Group className="mb-3">
                            {Array.from(new Set(firestoreProduct.product.attributes.map(attr => attr.name))).sort().map((key) => (
                                <div key={key}>
                                    <Form.Label>{key}</Form.Label>
                                    <Form.Control
                                        as="select"
                                        required
                                        onChange={(e) => handleAttributeSelection(key, e.target.value)}
                                    >
                                        <option value="" selected>Please Select ...</option>
                                        {firestoreProduct.product.attributes && firestoreProduct.product.attributes
                                            .filter(attr => attr.name === key)
                                            .sort((a, b) => a.value.localeCompare(b.value))
                                            .map((attr, index) => (
                                                <option key={index} value={attr.value}>{settings.hasPrice && attr.price && Number(attr.price) > 0 ? `${attr.value} (+${Utils.FormatCurrency(Number(attr.price), settings.currency)})` : attr.value}</option>
                                            ))}
                                    </Form.Control>
                                </div>
                            ))}
                        </Form.Group>
                    ) : null}

                    <InputGroup className="mb-3">
                        <Form.Control
                            type="number"
                            value={quantity}
                            min={1}
                            onChange={(e) => setQuantity(parseInt(e.target.value, 10))}
                        />
                        <Button variant="primary"
                            onClick={handleAddToCart}
                            disabled={addToCartLoading}
                        >{addToCartLoading ? <Spinner animation="border" size="sm" /> : "Add to Cart"}</Button>
                    </InputGroup>
                </Col>
            </Row>
        </Container>
    );
};

export default ProductView;